import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import Loader from "../../shared/components/Loader";
import ActionToast from "../../shared/components/ActionToast";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import EmailSentConfirmation from "./EmailSentConfirmation";
import { AccountRoutePaths } from "../routes";
import colors from "../../shared/themes/colors";
import { emailRegex } from "../../shared/validators";
import useTranslation from "../../shared/hooks/useTranslation";
import { ForgotPassword } from "../models/AccountModels";
import { hideNotification, resetState, sendForgotPasswordEmail } from "../forgotPasswordSlice";

const ForgotPasswordPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation("account");
  const rawResetPasswordEmailDescription = translate("resetPasswordEmailDescription");
  const rawResetPasswordEmailDescriptionParts = rawResetPasswordEmailDescription.split("{{email}}");


  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    isLoading,
    isEmailSent,
    formNotificationMessage,
    openSnackbar,
    alertMessage,
    severity,
    email,
  } = useAppSelector((state) => state.forgotPassword);

  const { register, handleSubmit, formState: { errors }, watch } = useForm<ForgotPassword>();
  const userName = watch("userName");

  useEffect(() => {
    dispatch(resetState());
  }, [dispatch]);

  const onSubmit = (data: ForgotPassword) => {
    if (!data.userName) return;
    dispatch(sendForgotPasswordEmail(data));
  };

  return (
    <>
      <ActionToast
        open={openSnackbar}
        variant={severity === "error" ? "error" : "success"}
        message={alertMessage}
        onClose={() => dispatch(hideNotification())}
        duration={6000}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingY: isMobile ? "32px" : "64px",
          backgroundColor: colors.secondary.gray98,
        }}
      >
        <Card
          sx={{
            maxWidth: "600px",
            width: "100%",
            margin: "16px",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            padding: isMobile ? "32px 16px 32px" : "64px 48px 64px",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                width: '100%',
                minHeight: '420px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Loader />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <MachineLogoIcon />
                <Box
                  sx={{
                    fontFamily: "Work Sans",
                    fontSize: isMobile ? "24px" : "36px",
                    fontWeight: "500",
                    lineHeight: isMobile ? "32px" : "42px",
                    letterSpacing: "-0.072px",
                    mt: 1.5,
                  }}
                >
                  {translate("resetPassword")}
                </Box>
              </Box>

              {!isEmailSent ? (
                <>
                  <CardContent sx={{ pt: 4 }}>
                    {formNotificationMessage && (
                      <Box className="mb-3">
                        <FormNotificationMessage {...formNotificationMessage} />
                      </Box>
                    )}

                    <Box
                      component="form"
                      id="forgotPasswordPageForm"
                      onSubmit={handleSubmit(onSubmit)}
                      className="flex flex-col gap-5"
                    >
                      <Box className="text-18-regular" color={colors.secondary.black}>
                        {translate("resetPasswordInstructions")}
                      </Box>
                      <Box className="flex flex-col">
                        <InputLabel>{translate("yourEmail")}</InputLabel>
                        <TextField
                          className="w-full"
                          {...register("userName", {
                            required: true,
                            pattern: emailRegex,
                          })}
                          error={!!errors.userName}
                          helperText={errors.userName ? translate("invalidEmail") : ""}
                        />
                      </Box>

                      <Box sx={{ width: "100%", padding: 0, mt: 2 }}>
                        <Button
                          fullWidth
                          type="submit"
                          variant="contained"
                          disabled={!userName || isLoading}
                        >
                          {translate("sendResetLink")}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      px: "16px",
                    }}
                  >
                    <Button
                      variant="text"
                      onClick={() => navigate(AccountRoutePaths.signIn)}
                      sx={{
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "24px",
                        letterSpacing: "-0.032px",
                        textDecoration: "underline",
                        textDecorationStyle: "solid",
                        textDecorationSkipInk: "auto",
                        textDecorationThickness: "auto",
                      }}
                    >
                      {translate("backToLogin")}
                    </Button>
                  </Box>
                </>
              ) : (
                <EmailSentConfirmation
                  open={true}
                  onClose={() => { window.location.assign(AccountRoutePaths.signIn); }}
                  title={translate("resetPasswordEmailSent")}
                  description={<>
                    {rawResetPasswordEmailDescriptionParts[0]}
                    <Box component={"span"} sx={{ fontWeight: 700, wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: '100%' }}>{email}</Box>
                    {rawResetPasswordEmailDescriptionParts[1]}
                  </>}
                />
              )}
            </>
          )}
        </Card>
      </Box>
    </>
  );
};

export default ForgotPasswordPage;


